/**
 * Custom form section for SwitchConfig — replaces the generic
 * array-field renderer with a table of cases (handle_id / label /
 * predicate) plus add / remove buttons.
 *
 * The standard NodeEditorDrawer can't auto-render `cases: list[SwitchCase]`
 * usefully — it'd produce a JSON textarea, which is exactly the
 * "developer-only" experience we're trying to escape. This component
 * lets the user think in terms of "first case wins" routing rules.
 *
 * Side-effects to be aware of:
 *   - Adding / removing / renaming cases changes the OUTPUT HANDLES of
 *     the node on the canvas (since outputHandlesFor reads config.cases).
 *   - Existing edges with source_handle pointing to a removed case
 *     become "orphan" — the runtime would 500 on them. The editor
 *     surfaces a warning when it detects orphans; cleanup is manual
 *     for now (will be auto in F-phase polish).
 */
import { GripVertical, Plus, Trash2 } from "lucide-react";
import * as React from "react";

import { ConditionEditor } from "@/components/apps/pageBuilder/ConditionEditor";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { CodeEditor } from "@/components/canonical/CodeEditor";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";


export interface SwitchCase {
  handle_id: string;
  label: string;
  predicate: string;
}


export interface SwitchCasesEditorProps {
  cases: SwitchCase[];
  defaultHandleId: string;
  onChange: (next: { cases: SwitchCase[]; default_handle_id: string }) => void;
  /** Edges currently using this node's handles. Drives the orphan
   *  warning when a case is removed/renamed. */
  outgoingEdgeHandles?: string[];
  /** `$json.<key>` bindings for the visual condition builder's field picker
   *  (same source as filter/if). */
  conditionFields?: string[];
  /** Ambient .d.ts for the Monaco predicate editor (Expression mode). */
  jsBindingsDts?: string;
}


// Sanitize a label into a usable handle_id (slug). Doesn't auto-write
// on every label change — only when the user creates a new case so they
// can override afterwards.
function slugify(s: string): string {
  return s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 32) || "case";
}


export function SwitchCasesEditor({
  cases,
  defaultHandleId,
  onChange,
  outgoingEdgeHandles = [],
  conditionFields = [],
  jsBindingsDts,
}: SwitchCasesEditorProps) {
  const validHandles = React.useMemo(() => {
    const s = new Set(cases.map((c) => c.handle_id));
    s.add(defaultHandleId || "default");
    return s;
  }, [cases, defaultHandleId]);

  const orphanHandles = outgoingEdgeHandles.filter((h) => !validHandles.has(h));

  function updateCase(idx: number, patch: Partial<SwitchCase>) {
    const next = cases.map((c, i) => (i === idx ? { ...c, ...patch } : c));
    onChange({ cases: next, default_handle_id: defaultHandleId });
  }

  function addCase() {
    const used = new Set(cases.map((c) => c.handle_id));
    let name = "case";
    let suffix = cases.length + 1;
    while (used.has(`${name}_${suffix}`)) suffix++;
    const handle_id = `${name}_${suffix}`;
    onChange({
      cases: [...cases, { handle_id, label: `Case ${suffix}`, predicate: "" }],
      default_handle_id: defaultHandleId,
    });
  }

  function removeCase(idx: number) {
    if (!window.confirm("Remove this case? Any edge attached to its handle will become orphaned.")) {
      return;
    }
    onChange({
      cases: cases.filter((_, i) => i !== idx),
      default_handle_id: defaultHandleId,
    });
  }

  function moveCase(idx: number, dir: -1 | 1) {
    const target = idx + dir;
    if (target < 0 || target >= cases.length) return;
    const next = [...cases];
    [next[idx], next[target]] = [next[target], next[idx]];
    onChange({ cases: next, default_handle_id: defaultHandleId });
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <Label>Cases</Label>
          <p className="text-[11px] text-muted-foreground">
            Predicates are evaluated top to bottom; first truthy one wins.
            Use the arrow buttons to reorder.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={addCase}>
          <Plus className="h-3.5 w-3.5" strokeWidth={1.5} />
          Add case
        </Button>
      </div>

      {orphanHandles.length > 0 && (
        <Alert tone="warning" title="Orphan edges">
          {orphanHandles.length} edge{orphanHandles.length > 1 ? "s" : ""} on
          this node point to handle{orphanHandles.length > 1 ? "s" : ""}{" "}
          <span className="font-mono">{orphanHandles.join(", ")}</span>{" "}
          that no longer exist. Either re-add a case with the matching
          handle id, or delete the dangling edge from the canvas.
        </Alert>
      )}

      <div className="space-y-2">
        {cases.map((c, idx) => (
          <CaseRow
            key={idx}
            case_={c}
            idx={idx}
            total={cases.length}
            conditionFields={conditionFields}
            jsBindingsDts={jsBindingsDts}
            onChange={(patch) => updateCase(idx, patch)}
            onRemove={() => removeCase(idx)}
            onMoveUp={() => moveCase(idx, -1)}
            onMoveDown={() => moveCase(idx, +1)}
          />
        ))}
        {cases.length === 0 && (
          <div className="rounded-md border border-dashed border-border/60 px-3 py-4 text-center text-[11px] text-muted-foreground">
            No cases yet. Add one — every Switch needs at least one
            case before it can route messages.
          </div>
        )}
      </div>

      <div className="border-t border-border/60 pt-3 space-y-1.5">
        <Label htmlFor="switch-default-id">Default handle id</Label>
        <Input
          id="switch-default-id"
          value={defaultHandleId}
          onChange={(e) =>
            onChange({ cases, default_handle_id: slugify(e.target.value) })
          }
          placeholder="default"
        />
        <p className="text-[11px] text-muted-foreground">
          Routed-to when no case matches. Always present as an output handle.
        </p>
      </div>
    </div>
  );
}


function CaseRow({
  case_,
  idx,
  total,
  conditionFields,
  jsBindingsDts,
  onChange,
  onRemove,
  onMoveUp,
  onMoveDown,
}: {
  case_: SwitchCase;
  idx: number;
  total: number;
  conditionFields: string[];
  jsBindingsDts?: string;
  onChange: (patch: Partial<SwitchCase>) => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}) {
  return (
    <div className="rounded-md border border-border/60 bg-muted/30 p-2 space-y-2">
      <div className="flex items-center gap-1">
        <div className="flex flex-col text-muted-foreground/60">
          <button
            type="button"
            disabled={idx === 0}
            onClick={onMoveUp}
            className="text-[8px] leading-none hover:text-foreground disabled:opacity-30"
            title="Move up"
          >
            ▲
          </button>
          <button
            type="button"
            disabled={idx === total - 1}
            onClick={onMoveDown}
            className="text-[8px] leading-none hover:text-foreground disabled:opacity-30"
            title="Move down"
          >
            ▼
          </button>
        </div>
        <GripVertical className="h-3 w-3 text-muted-foreground/60" strokeWidth={1.5} />
        <span className="font-mono text-[10px] text-muted-foreground">#{idx + 1}</span>

        <div className="ml-1 flex-1">
          <Input
            value={case_.label}
            onChange={(e) => onChange({ label: e.target.value })}
            placeholder="Label (shown on canvas)"
            className="h-7 text-xs"
          />
        </div>
        <div className="w-40">
          <Input
            value={case_.handle_id}
            onChange={(e) =>
              onChange({
                handle_id: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ""),
              })
            }
            placeholder="handle_id"
            className="h-7 font-mono text-[10px]"
            title="Handle id — used on outgoing edges (lowercase, _ or -)"
          />
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={onRemove}
          aria-label="Remove case"
        >
          <Trash2 className="h-3.5 w-3.5 text-danger" strokeWidth={1.5} />
        </Button>
      </div>
      {/* Same visual condition builder as filter/if — RAW JS mode (no braces),
          Monaco for Expression mode. Predicate compiles to truthy/falsy. */}
      <ConditionEditor
        value={case_.predicate ?? ""}
        onChange={(v) => onChange({ predicate: v })}
        fields={conditionFields}
        braces={false}
        renderExpression={(v, oc) => (
          <div className="h-24 w-full">
            <CodeEditor
              value={v}
              onChange={oc}
              language="javascript"
              height="100%"
              inline
              jsAmbientTypes={jsBindingsDts}
            />
          </div>
        )}
      />
    </div>
  );
}
